import React from 'react';
import { X, AlertTriangle, MapPin, XCircle } from 'lucide-react';

/**
 * Popup para mostrar alertas de geocodificación después de crear una ruta
 */
const GeocodingAlertsPopup = ({ alerts, onClose }) => {
    if (!alerts || (alerts.withAlerts.length === 0 && alerts.failed.length === 0)) {
        return null;
    }

    const totalAlerts = alerts.withAlerts.length + alerts.failed.length;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-orange-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                Alertas de Geocodificación
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {totalAlerts} {totalAlerts === 1 ? 'pedido requiere' : 'pedidos requieren'} atención
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Pedidos con alertas pero geocod ificados */}
                    {alerts.withAlerts.length > 0 && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <MapPin className="w-5 h-5 text-orange-600" />
                                <h3 className="font-bold text-gray-900">
                                    Pedidos con Alertas (Incluidos en Ruta)
                                </h3>
                                <span className="text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                                    {alerts.withAlerts.length}
                                </span>
                            </div>
                            <div className="space-y-3">
                                {alerts.withAlerts.map((order, idx) => (
                                    <div
                                        key={idx}
                                        className="bg-orange-50 border border-orange-200 rounded-lg p-4"
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <div className="font-semibold text-gray-900">
                                                    {order.order_number}
                                                </div>
                                                <div className="text-sm text-gray-700">
                                                    {order.customer_name}
                                                </div>
                                            </div>
                                            <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded">
                                                {order.method === 'ai_assisted' ? 'AI Asistido' : 'Otra Ciudad'}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600 mb-2">
                                            📍 {order.address}
                                        </div>
                                        <div className="text-sm text-orange-700 bg-orange-100 px-3 py-2 rounded">
                                            ⚠️ {order.alert}
                                        </div>
                                        {order.latitude && order.longitude && (
                                            <div className="text-xs text-gray-500 mt-2">
                                                Coords: {order.latitude.toFixed(6)}, {order.longitude.toFixed(6)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Pedidos fallidos */}
                    {alerts.failed.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <XCircle className="w-5 h-5 text-red-600" />
                                <h3 className="font-bold text-gray-900">
                                    Pedidos NO Geocodificados (Excluidos)
                                </h3>
                                <span className="text-sm bg-red-100 text-red-700 px-2 py-1 rounded-full">
                                    {alerts.failed.length}
                                </span>
                            </div>
                            <div className="space-y-3">
                                {alerts.failed.map((order, idx) => (
                                    <div
                                        key={idx}
                                        className="bg-red-50 border border-red-200 rounded-lg p-4"
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <div className="font-semibold text-gray-900">
                                                    {order.order_number}
                                                </div>
                                                <div className="text-sm text-gray-700">
                                                    {order.customer_name}
                                                </div>
                                            </div>
                                            <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">
                                                FALLIDO
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600 mb-2">
                                            📍 {order.address}
                                        </div>
                                        <div className="text-sm text-red-700 bg-red-100 px-3 py-2 rounded">
                                            ❌ {order.alert}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="flex items-start gap-3 mb-4">
                        <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-700">
                            <p className="font-semibold mb-1">Recomendaciones:</p>
                            <ul className="list-disc list-inside space-y-1 text-gray-600">
                                <li>Pedidos con alertas (naranja): Verificar ubicación con cliente</li>
                                <li>Pedidos fallidos (rojo): Contactar cliente para corregir dirección</li>
                                <li>Si un pedido no es de Bogotá, eliminarlo de la ruta</li>
                            </ul>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GeocodingAlertsPopup;
